import { MapPin, Phone, ChevronRight, Check } from "lucide-react";
import { Link } from "react-router-dom";

const AreaCard = ({ area }) => {
  const features = area.features || [];
  const areaId = area.name.toLowerCase().replace(/ /g, "-");
  
  return (
    <div
      id={areaId}
      className="bg-card border-2 border-secondary/20 rounded-sm overflow-hidden hover:border-accent transition-colors"
      data-testid={`area-card-${areaId}`}
    >
      <div className="bg-primary p-6">
        <div className="flex items-center gap-3">
          <MapPin className="h-6 w-6 text-accent" />
          <h2 className="font-heading font-bold text-xl uppercase text-primary-foreground">
            {area.name}
          </h2>
        </div>
      </div>
      <div className="p-6">
        <p className="text-muted-foreground font-body mb-4">
          {area.description}
        </p>
        <ul className="space-y-2">
          {features.map((feature, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-accent flex-shrink-0" />
              <span className="text-foreground font-body">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ServiceAreasPage = () => {
  const serviceAreas = [
    {
      name: "Prince Edward County",
      description: "Full service coverage including Picton, Wellington, Bloomfield, and surrounding areas.",
      features: ["Residential wildlife control", "Commercial services", "Emergency response"]
    },
    {
      name: "Belleville",
      description: "Complete wildlife control services for Belleville and the surrounding region.",
      features: ["Urban wildlife removal", "Attic cleanup services", "Prevention programs"]
    },
    {
      name: "Trenton",
      description: "Serving Trenton and Quinte West with professional wildlife management.",
      features: ["Fast response times", "Full exclusion services", "Insulation replacement"]
    },
    {
      name: "Brighton",
      description: "Wildlife control services for Brighton and the surrounding countryside.",
      features: ["Rural property services", "Beaver management", "Multi-animal removal"]
    },
    {
      name: "Tweed",
      description: "Covering Tweed and northern areas with comprehensive wildlife solutions.",
      features: ["Large property coverage", "Wildlife prevention", "Seasonal services"]
    },
    {
      name: "Napanee",
      description: "Professional wildlife control for Greater Napanee and Lennox and Addington.",
      features: ["Residential and commercial", "Emergency service", "Complete solutions"]
    }
  ];

  return (
    <div data-testid="service-areas-page">
      <section className="bg-primary py-16 lg:py-24" data-testid="areas-hero">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-primary-foreground mb-6">
              Service Areas
            </h1>
            <p className="text-primary-foreground/80 font-body text-lg">
              Proudly serving communities across Eastern Ontario. From Prince Edward County to Napanee, 
              we provide professional wildlife control services to protect your home and property.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-secondary/10" data-testid="areas-overview">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {serviceAreas.map((area) => {
              const areaId = area.name.toLowerCase().replace(/ /g, "-");
              return (
                <a
                  key={area.name}
                  href={`#${areaId}`}
                  className="flex items-center gap-2 bg-card border-2 border-secondary/20 px-5 py-3 rounded-sm hover:border-accent transition-colors"
                  data-testid={`area-quick-link-${areaId}`}
                >
                  <MapPin className="h-4 w-4 text-accent" />
                  <span className="font-heading text-sm uppercase tracking-wide text-foreground">{area.name}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4" data-testid="areas-list">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceAreas.map((area) => (
              <AreaCard key={area.name} area={area} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-primary/5" data-testid="areas-contact">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-6">
            Not Sure If We Cover Your Area?
          </h2>
          <p className="text-muted-foreground font-body text-lg mb-8">
            Give us a call! We may be able to service locations outside our primary coverage areas. 
            We are always happy to discuss your wildlife control needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:343-263-2000"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide rugged-shadow transition-all hover:bg-primary/90"
              data-testid="areas-call-btn"
            >
              <Phone className="h-4 w-4" />
              Call 343-263-2000
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary text-primary px-8 py-4 rounded-sm font-heading uppercase tracking-wide transition-all hover:bg-primary hover:text-primary-foreground"
              data-testid="areas-contact-btn"
            >
              Send Message
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-primary" data-testid="areas-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary-foreground mb-6">
            Local Wildlife Problem?
          </h2>
          <p className="text-primary-foreground/80 font-body text-lg mb-4">
            We are based right here in Plainfield, Ontario - which means fast response times for all our service areas.
          </p>
          <p className="text-primary-foreground/60 font-body mb-8">
            529 Thrasher Rd, Plainfield, ON K0K 2V0
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide rugged-shadow transition-all hover:bg-accent/90"
            data-testid="areas-cta-btn"
          >
            Get Your Free Quote
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreasPage;
